import React, { useEffect, useMemo, useState } from "react";
import { GridColDef } from "@mui/x-data-grid";
import { Box, Chip, Stack, TableContainer, Typography } from "@mui/material";
import { useSelector } from "../../store";
import TableActions from "../base/TableActions";
import DataGridTable from "../base/DataGridTable";
import { buildUserRowMetadata } from "../../helpers/userRowUtils";

interface Props {
  handleEdit?: (id: number) => void;
  handleDelete?: (id: number) => void;
  handleInvite?: (id: number) => void;
  handleAppRoute?: (id: number, url: string) => void;
}

const UserTable: React.FC<Props> = (props) => {
  const { handleEdit, handleDelete, handleInvite, handleAppRoute } = props;

  const { userList, status } = useSelector((store) => store.user);
  const { dbUser } = useSelector((store) => store.auth);
  const { enabledApplications } = useSelector((store) => store.application);
  const { licenses } = useSelector((store) => store.company);

  const [columns, setColumns] = useState<GridColDef[]>([]);

  const userMetadata = useMemo(
    () =>
      buildUserRowMetadata(
        userList || [],
        enabledApplications,
        dbUser,
        handleAppRoute,
        licenses,
      ),
    [userList, enabledApplications, dbUser, handleAppRoute, licenses],
  );

  const rows = useMemo(
    () =>
      userMetadata.map(({ user, canEdit, appRoutes }) => ({
        id: user.id,
        canEdit,
        name: `${user.firstName} ${user.lastName}`,
        email: user.email,
        roles: (
          <Box
            display="flex"
            flexDirection="row"
            flexWrap="wrap"
            justifyContent="center"
          >
            {user.roles?.map((x) => (
              <Chip
                key={x.id}
                label={x.value}
                size="small"
                sx={{ m: 0.25 }}
                color="info"
              />
            ))}
          </Box>
        ),
        actions: (
          <TableActions
            id={user.id!}
            handleEdit={
              canEdit && handleEdit ? () => handleEdit!(user.id!) : undefined
            }
            handleDelete={
              canEdit && handleDelete
                ? () => handleDelete!(user.id!)
                : undefined
            }
            handleInvite={
              canEdit && handleInvite
                ? () => handleInvite!(user.id!)
                : undefined
            }
          />
        ),
        routes:
          canEdit && appRoutes.length ? (
            <TableActions id={user.id!} handleAppRoutes={appRoutes} />
          ) : (
            <></>
          ),
      })),
    [userMetadata, handleDelete, handleEdit, handleInvite],
  );

  const hasEditableUser = userMetadata.some((r) => r.canEdit);

  useEffect(() => {
    const baseColumns: GridColDef[] = [
      {
        field: "name",
        headerName: "Name",
        flex: 0.75,
        headerAlign: "center",
        align: "center",
      },
      {
        field: "email",
        headerName: "Email",
        flex: 1,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => (
          <Box sx={{ whiteSpace: "pre-line", overflowWrap: "anywhere" }}>
            {params.value}
          </Box>
        ),
      },
      {
        field: "roles",
        headerName: "Roles",
        flex: 0.75,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => (
          <Stack direction="row" flexWrap="wrap">
            {params.value}
          </Stack>
        ),
      },
    ];

    if (status === "idle" && rows.length === userList.length) {
      if (hasEditableUser) {
        baseColumns.push(
          {
            field: "actions",
            headerName: "Actions",
            flex: 0.5,
            sortable: false,
            disableColumnMenu: true,
            disableExport: true,
            renderCell: (params) => <>{params.value}</>,
          },
          {
            field: "routes",
            headerName: "Edit in App",
            flex: 0.5,
            sortable: false,
            disableColumnMenu: true,
            disableExport: true,
            renderCell: (params) => <>{params.value}</>,
          },
        );
      }

      setColumns(baseColumns);
    }
  }, [hasEditableUser, rows.length, status, userList]);

  return (
    <TableContainer>
      <DataGridTable
        columns={columns || []}
        rows={rows}
        loading={status === "loading" && !userList.length}
      />
    </TableContainer>
  );
};

export default UserTable;
